import { GrantCategory } from '../types/grant';

export interface ConsultantResponse {
  category: GrantCategory;
  suggestedFilters: Record<string, string | string[]>;
  summary: string;
  reasoning: string;
  recommendedApp?: string;
}

export const CONSULTANT_SYSTEM_PROMPT = `
You are an expert Canadian Grant Consultant.
Analyze the user's story and map it to ONE of these categories:
['disability', 'small-business', 'indigenous', 'housing', 'education', 'arts'].
Return a JSON object only.
`;

export async function analyzeUserNeeds(userStory: string): Promise<ConsultantResponse> {
  // Simulate AI thinking delay
  await new Promise((resolve) => setTimeout(resolve, 1500));

  const story = userStory.toLowerCase();

  // Disability keywords
  if (
    story.includes('wheelchair') ||
    story.includes('ramp') ||
    story.includes('disability') ||
    story.includes('assistive') ||
    story.includes('mobility') ||
    story.includes('accessible') ||
    story.includes('aish') ||
    story.includes('odsp') ||
    story.includes('pwd')
  ) {
    return {
      category: 'disability',
      suggestedFilters: { disabilityType: 'physical', needType: 'assistive-device' },
      summary:
        'You are looking for support with accessibility and mobility modifications or assistive devices.',
      reasoning: "Detected keywords related to disability support and accessibility.",
      recommendedApp: 'canability-grants',
    };
  }

  // Small Business keywords
  if (
    story.includes('business') ||
    story.includes('startup') ||
    story.includes('entrepreneur') ||
    story.includes('company') ||
    story.includes('sell') ||
    story.includes('employee') ||
    story.includes('revenue')
  ) {
    return {
      category: 'small-business',
      suggestedFilters: { businessStage: 'startup', fundingType: 'grant' },
      summary: 'You are looking for seed funding to launch or grow your business.',
      reasoning: 'Detected entrepreneurial intent and business-related keywords.',
      recommendedApp: 'small-business-grants',
    };
  }

  // Indigenous keywords
  if (
    story.includes('first nation') ||
    story.includes('indigenous') ||
    story.includes('métis') ||
    story.includes('metis') ||
    story.includes('inuit') ||
    story.includes('aboriginal') ||
    story.includes('treaty')
  ) {
    return {
      category: 'indigenous',
      suggestedFilters: { indigenousIdentity: 'first-nations' },
      summary:
        'You are looking for funding opportunities specifically for Indigenous individuals or communities.',
      reasoning: 'Detected Indigenous identity or community keywords.',
    };
  }

  // Housing keywords
  if (
    story.includes('house') ||
    story.includes('home') ||
    story.includes('renovation') ||
    story.includes('rent') ||
    story.includes('energy') ||
    story.includes('heating') ||
    story.includes('insulation')
  ) {
    return {
      category: 'housing',
      suggestedFilters: { housingType: 'owner-occupied', renovationType: 'accessibility' },
      summary:
        'You are looking for housing-related funding such as renovations or energy efficiency upgrades.',
      reasoning: 'Detected housing and home improvement keywords.',
    };
  }

  // Education keywords
  if (
    story.includes('school') ||
    story.includes('university') ||
    story.includes('college') ||
    story.includes('education') ||
    story.includes('scholarship') ||
    story.includes('training') ||
    story.includes('learn')
  ) {
    return {
      category: 'education',
      suggestedFilters: { educationLevel: 'post-secondary' },
      summary:
        'You are looking for educational funding such as scholarships, bursaries, or training grants.',
      reasoning: 'Detected education and learning-related keywords.',
    };
  }

  // Arts keywords
  if (
    story.includes('art') ||
    story.includes('music') ||
    story.includes('film') ||
    story.includes('theatre') ||
    story.includes('creative') ||
    story.includes('culture') ||
    story.includes('performance')
  ) {
    return {
      category: 'arts',
      suggestedFilters: { artForm: 'visual-arts' },
      summary: 'You are looking for funding to support artistic or cultural projects.',
      reasoning: 'Detected arts and culture-related keywords.',
    };
  }

  // Default fallback
  return {
    category: 'small-business',
    suggestedFilters: {},
    summary:
      "Based on your description, let's explore Business grants first, which cover a wide range of funding opportunities.",
    reasoning: 'Defaulting to broadest category for general funding needs.',
    recommendedApp: 'cangrant-search',
  };
}
